import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { areaLabel, type AreaRow } from '../data/geo.ts'
import { areaShort } from '../data/planUi.ts'

const GROUPS = new Set(['loop', 'compact_course', 'practice', 'nursery'])

/** Pick the areas of a job from the course. Only the kinds the task type applies to are offered (none given = every kind); they are grouped under
 *  their loop and ordered by hole, with "all / none" per group: mowing eighteen greens is two taps, not eighteen. */
export function AreaPicker({ areas, kinds, value, onChange, lang }: { areas: AreaRow[]; kinds: readonly string[]; value: string[]; onChange: (ids: string[]) => void; lang: string }) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const byId = useMemo(() => new Map(areas.map((a) => [a.id, a])), [areas])
  const groups = useMemo(() => {
    const holeOf = (a: AreaRow): AreaRow | undefined => { for (let p: AreaRow | undefined = a; p; p = p.parent_id ? byId.get(p.parent_id) : undefined) if (p.area_type_code === 'hole') return p; return undefined }
    const loopOf = (a: AreaRow): AreaRow | undefined => { for (let p: AreaRow | undefined = a; p; p = p.parent_id ? byId.get(p.parent_id) : undefined) if (GROUPS.has(p.area_type_code)) return p; return undefined }
    const fits = (a: AreaRow) => !a.archived_at && a.area_type_code !== 'site_root' && (kinds.length === 0 || kinds.includes(a.area_type_code))
    const out = new Map<string, { key: string; label: string; sort: number; items: AreaRow[] }>()
    for (const a of areas.filter(fits)) {
      const l = loopOf(a)
      const g = out.get(l?.id ?? '') ?? { key: l?.id ?? '', label: l ? areaLabel(l, lang) : t('plan.areaOther'), sort: l?.sort_key ?? 999, items: [] }
      g.items.push(a); out.set(l?.id ?? '', g)
    }
    for (const g of out.values()) g.items.sort((x, y) => (holeOf(x)?.hole_number ?? 999) - (holeOf(y)?.hole_number ?? 999) || x.sort_key - y.sort_key || areaLabel(x, lang).localeCompare(areaLabel(y, lang), lang))
    return [...out.values()].sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label, lang))
  }, [areas, byId, kinds, lang, t])
  const set = new Set(value)
  const needle = q.trim().toLowerCase()
  const toggle = (id: string) => onChange(set.has(id) ? value.filter((x) => x !== id) : [...value, id])
  return (
    <div className="flex flex-col gap-2" data-testid="area-picker">
      <input type="search" className="input !min-h-11" placeholder={t('plan.areaSearch')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('plan.areaSearch')} data-testid="area-search" />
      <p className="text-sm font-semibold" data-testid="area-count">{t('plan.areaChosen', { count: value.length })}</p>
      {groups.length === 0 && <p className="text-sm opacity-70">{t('plan.areaNone')}</p>}
      {groups.map((g) => {
        const items = needle ? g.items.filter((a) => areaShort(a, byId, lang, t).toLowerCase().includes(needle) || (a.code ?? '').toLowerCase().includes(needle)) : g.items
        if (items.length === 0) return null
        const all = items.every((a) => set.has(a.id))
        return (
          <fieldset key={g.key} className="rounded-xl border-2 border-slate-300 p-2 dark:border-slate-600" data-testid="area-group">
            <legend className="px-1 text-sm font-bold">{g.label}</legend>
            <button type="button" className="btn mb-1 !min-h-11 !px-4" onClick={() => onChange(all ? value.filter((id) => !items.some((a) => a.id === id)) : [...new Set([...value, ...items.map((a) => a.id)])])} data-testid="area-all">{all ? t('plan.areaNoneOf') : t('plan.areaAllOf', { count: items.length })}</button>
            <ul className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
              {items.map((a) => (
                <li key={a.id}><label className="flex min-h-11 items-center gap-2"><input type="checkbox" className="size-5" checked={set.has(a.id)} onChange={() => toggle(a.id)} data-testid="area-opt" data-area-code={a.code ?? ''} />{areaShort(a, byId, lang, t)}</label></li>
              ))}
            </ul>
          </fieldset>
        )
      })}
    </div>
  )
}
